import {
  Button,
  NumericInput,
  RangeSlider,
  Typography,
} from "@repo/design-system";
import type {
  IRangeBound,
  RangeColumnsForFiltersForDTO,
} from "@/api/molecularData";
import {
  decimalsForStep,
  type IRangeFieldConfig,
} from "@/lib/filterFieldConfig";

interface IRangeFilterRowProps {
  field: RangeColumnsForFiltersForDTO;
  config: IRangeFieldConfig;
  bound: IRangeBound;
  value: { min: number; max: number } | undefined;
  onChange: (
    field: RangeColumnsForFiltersForDTO,
    next: { min: number; max: number } | undefined,
  ) => void;
}

export function RangeFilterRow({
  field,
  config,
  bound,
  value,
  onChange,
}: IRangeFilterRowProps) {
  const current = value ?? { min: bound.min, max: bound.max };
  const decimals = decimalsForStep(config.step);
  const isActive = value !== undefined;

  // Slider drags are already guaranteed sane (within bound, min <= max) by
  // RangeSlider itself — clamping here is just a defensive no-op.
  const commitFromSlider = (min: number, max: number) => {
    const clampedMin = Math.max(bound.min, Math.min(min, max));
    const clampedMax = Math.min(bound.max, Math.max(min, max));
    onChange(field, { min: clampedMin, max: clampedMax });
  };

  // Box edits propagate exactly what the user typed, unclamped. Clamping
  // here would fight backspace / select-all-to-retype: any intermediate
  // keystroke that reads as out of range (e.g. the "5" while typing "500"
  // against a dataset min of 76) would get silently snapped back before the
  // user can finish typing. An out-of-range or inverted value sent to the
  // backend is harmless — it just matches zero extra rows, not a crash — so
  // there's nothing worth enforcing live. The slider below clamps its own
  // display independently, so it can't visually break from this.
  const commitFromBox = (min: number, max: number) =>
    onChange(field, { min, max });

  const clampToBound = (v: number) =>
    Math.min(bound.max, Math.max(bound.min, v));
  const sliderMin = clampToBound(Math.min(current.min, current.max));
  const sliderMax = clampToBound(Math.max(current.min, current.max));

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-borderPrimary p-3">
      <div className="flex items-center justify-between">
        <Typography
          variant="paragraph-m-semibold"
          className="text-contentPrimary">
          {config.label}
        </Typography>
        {isActive && (
          <Button
            variant="transparent"
            size="small"
            leftIcon="arrow-rotate"
            onPress={() => onChange(field, undefined)}>
            Reset
          </Button>
        )}
      </div>

      <RangeSlider
        selectedValues={[sliderMin, sliderMax]}
        minValue={bound.min}
        maxValue={bound.max}
        step={config.step}
        thumbLabels={["Min", "Max"]}
        formatValue={(v) => v.toFixed(decimals)}
        onChange={([min, max]) => commitFromSlider(min, max)}
        onChangeEnd={([min, max]) => commitFromSlider(min, max)}
      />

      <div className="flex items-center gap-2">
        <NumericInput
          value={current.min}
          min={bound.min}
          max={bound.max}
          step={config.step}
          className="w-full"
          onChange={(min) => commitFromBox(min, current.max)}
        />
        <Typography
          variant="paragraph-m-regular"
          className="text-contentTertiary">
          –
        </Typography>
        <NumericInput
          value={current.max}
          min={bound.min}
          max={bound.max}
          step={config.step}
          className="w-full"
          onChange={(max) => commitFromBox(current.min, max)}
        />
      </div>
    </div>
  );
}
